/**
 * EnvironmentalMeasurementFilters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { TextField } from '@mui/material';
import { InlineFilterBar, InlineSelect } from '../../../components/common';
import type { InlineSelectOption } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';

export interface EnvironmentalMeasurementFiltersData {
  searchTerm: string;
  companyId: string;
  departmentId: string;
  occupationId: string;
  riskId: string;
  measurementEquipmentId: string;
  startDate: string;
  endDate: string;
  exceedsLimit: string;
  hazardousFraming: string;
}

export interface NamedOption {
  id: number;
  name: string;
}

interface Props {
  onFilter: (filters: EnvironmentalMeasurementFiltersData) => void;
  companies?: NamedOption[];
  departments?: NamedOption[];
  occupations?: NamedOption[];
  risks?: NamedOption[];
  equipments?: NamedOption[];
  loading?: boolean;
  total?: number;
  actions?: React.ReactNode;
}

const defaultValues: EnvironmentalMeasurementFiltersData = {
  searchTerm: '',
  companyId: '',
  departmentId: '',
  occupationId: '',
  riskId: '',
  measurementEquipmentId: '',
  startDate: '',
  endDate: '',
  exceedsLimit: '',
  hazardousFraming: '',
};

const SIM_NAO_OPTIONS: InlineSelectOption[] = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

const toOptions = (items: NamedOption[]): InlineSelectOption[] =>
  items.map((item) => ({ value: String(item.id), label: item.name }));

const nameOf = (items: NamedOption[], id: string): string =>
  items.find((item) => String(item.id) === id)?.name ?? id;

export const EnvironmentalMeasurementFiltersComponent: React.FC<Props> = ({
  onFilter,
  companies = [],
  departments = [],
  occupations = [],
  risks = [],
  equipments = [],
  loading = false,
  total,
  actions,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      companyId: 'Empresa',
      departmentId: 'Setor',
      occupationId: 'Função',
      riskId: 'Risco',
      measurementEquipmentId: 'Equipamento',
      startDate: 'Medido a partir de',
      endDate: 'Medido até',
      exceedsLimit: 'Excedente (acima do limite)',
      hazardousFraming: 'Enquadramento',
    },
    formatValue: {
      companyId: (v: string) => nameOf(companies, v),
      departmentId: (v: string) => nameOf(departments, v),
      occupationId: (v: string) => nameOf(occupations, v),
      riskId: (v: string) => nameOf(risks, v),
      measurementEquipmentId: (v: string) => nameOf(equipments, v),
      exceedsLimit: (v: string) => (v === 'true' ? 'Sim' : 'Não'),
      hazardousFraming: (v: string) => (v === 'true' ? 'Sim' : 'Não'),
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por risco, técnica, observação..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="medições"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
      actions={actions}
    >
      {companies.length > 0 && (
        <InlineSelect
          label="Empresa"
          value={filters.companyId}
          options={toOptions(companies)}
          onChange={(v) => setFilter('companyId', v)}
          emptyLabel="Todas"
          minWidth={160}
        />
      )}

      {departments.length > 0 && (
        <InlineSelect
          label="Setor"
          value={filters.departmentId}
          options={toOptions(departments)}
          onChange={(v) => setFilter('departmentId', v)}
          emptyLabel="Todos"
          minWidth={150}
        />
      )}

      {occupations.length > 0 && (
        <InlineSelect
          label="Função"
          value={filters.occupationId}
          options={toOptions(occupations)}
          onChange={(v) => setFilter('occupationId', v)}
          emptyLabel="Todas"
          minWidth={150}
        />
      )}

      {risks.length > 0 && (
        <InlineSelect
          label="Risco"
          value={filters.riskId}
          options={toOptions(risks)}
          onChange={(v) => setFilter('riskId', v)}
          emptyLabel="Todos"
          minWidth={170}
        />
      )}

      {equipments.length > 0 && (
        <InlineSelect
          label="Equipamento"
          value={filters.measurementEquipmentId}
          options={toOptions(equipments)}
          onChange={(v) => setFilter('measurementEquipmentId', v)}
          emptyLabel="Todos"
          minWidth={170}
        />
      )}

      <InlineSelect
        label="Excedente"
        value={filters.exceedsLimit}
        options={SIM_NAO_OPTIONS}
        onChange={(v) => setFilter('exceedsLimit', v)}
        emptyLabel="Todos"
        minWidth={130}
      />

      <InlineSelect
        label="Enquadramento"
        value={filters.hazardousFraming}
        options={SIM_NAO_OPTIONS}
        onChange={(v) => setFilter('hazardousFraming', v)}
        emptyLabel="Todos"
        minWidth={150}
      />

      <TextField
        label="Medido a partir de"
        type="date"
        size="small"
        value={filters.startDate}
        onChange={(e) => setFilter('startDate', e.target.value)}
        InputLabelProps={{ shrink: true }}
        disabled={loading}
        sx={{ minWidth: 170 }}
      />

      <TextField
        label="Medido até"
        type="date"
        size="small"
        value={filters.endDate}
        onChange={(e) => setFilter('endDate', e.target.value)}
        InputLabelProps={{ shrink: true }}
        disabled={loading}
        sx={{ minWidth: 170 }}
      />
    </InlineFilterBar>
  );
};

export default EnvironmentalMeasurementFiltersComponent;
